import {
  Box,
  Button,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import { LeadFormActions } from '../../../Services/_actions';
import CommonTable from '../../Shared/Tables/TableV1.tsx';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ConfirmBox from '../../FlowBuilder/Common/ConfirmBox/index.tsx';

const HelpFile = () => {
  const { leadFormId } = useParams();
  const { showSnackBar } = useSnackBar();
  const actions = LeadFormActions();
  const [helpFiles, setHelpFiles] = useState<any[]>([]);
  const [helpFile, setHelpFile] = useState<File | null>(null);
  const [helpFileName, setHelpFileName] = useState<string>('');
  const [isUploadingHelp, setIsUploadingHelp] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<any | null>(null);
  const helpFileInputRef = useRef<HTMLInputElement | null>(null);

  const handleEdit = (file: any) => {
    if (!file) return;
    setHelpFileName(file.file_name || '');
    setHelpFile(null);
  };

  const handleDeleteClick = (file: any) => {
    setFileToDelete(file);
    setConfirmOpen(true);
  };

  const handleConfirmClose = (confirmed: boolean | { isDenied?: boolean }) => {
    setConfirmOpen(false);
    if (confirmed === true && fileToDelete) {
      deleteHelpFile(fileToDelete);
    }
  };

  // Actual delete function
  const deleteHelpFile = async (file: any) => {
    if (!file?._id) return;

    try {
      const res = await actions.deleteHelpFile(file._id);
      if (res?.status === 200 || res?.data?.status) {
        showSnackBar('Help file deleted successfully.', 'success');
        if (leadFormId) {
          getHelpFiles(leadFormId);
        }
      } else {
        showSnackBar('Failed to delete help file.', 'error');
      }
    } catch (err) {
      console.error('Delete error:', err);
      showSnackBar('Error deleting help file.', 'error');
    }
  };

  const handleUpload = useCallback(async () => {
    try {
      if (!leadFormId) {
        showSnackBar('Please save the form first (Lead Form ID missing).', 'warning');
        return;
      }
      if (!helpFile) {
        showSnackBar('Please choose a .xlsx/.xls file.', 'warning');
        return;
      }
      setIsUploadingHelp(true);

      const finalName = helpFileName || helpFile?.name || 'help file';

      const res: any = await actions.uploadHelpFile(leadFormId, helpFile, finalName);
      if (res?.status === 200 || res?.data?.status) {
        showSnackBar('Help file uploaded successfully.', 'success');
        setHelpFile(null);
        setHelpFileName('');
        // refresh list
        getHelpFiles(leadFormId as string);
      } else {
        showSnackBar('Failed to upload help file.', 'error');
      }
    } catch (err) {
      console.error('Upload error:', err);
      showSnackBar('Error uploading help file.', 'error');
    } finally {
      setIsUploadingHelp(false);
    }
  }, [leadFormId, helpFile, helpFileName, actions, showSnackBar]);

  const getHelpFiles = async (leadsFormId: string) => {
    try {
      const response = await actions.getHelpFiles(leadsFormId);
      if (response?.data && response.data.length > 0) {
        setHelpFiles(response.data);
      } else {
        setHelpFiles([]);
        showSnackBar('No help files available', 'info');
      }
    } catch (error) {
      console.error('Error fetching help files:', error);
      showSnackBar('Error loading help files', 'error');
    }
  };

  useEffect(() => {
    if (leadFormId) {
      getHelpFiles(leadFormId as string);
    }
  }, [leadFormId]);

  const tableData = helpFiles;

  const columns = [
    {
      accessorKey: '_id',
      header: 'File ID',
      enableSorting: false,
      Cell: ({ cell }: any) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
          {cell.getValue()}
        </Typography>
      ),
    },
    {
      accessorKey: 'file_name',
      header: 'File Name',
      enableSorting: true,
      Cell: ({ cell }: any) => <Typography variant="body2">{cell.getValue() || '-'}</Typography>,
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      enableSorting: false,
      Cell: ({ row }) => {
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
        const open = Boolean(anchorEl);

        const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
          setAnchorEl(event.currentTarget);
        };

        const handleMenuClose = () => {
          setAnchorEl(null);
        };

        return (
          <>
            <IconButton onClick={handleMenuOpen}>
              <MoreVertIcon />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
              <MenuItem
                onClick={() => {
                  handleEdit(row.original);
                  handleMenuClose();
                }}
              >
                Edit
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleDeleteClick(row.original);
                  handleMenuClose();
                }}
              >
                Delete
              </MenuItem>
            </Menu>
          </>
        );
      },
    },
  ];

  return (
    <Box
      sx={{
        padding: '5px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Grid container spacing={2} sx={{ alignItems: 'center' }}>
        <Grid item xs={6}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Upload Help File
          </Typography>
        </Grid>
      </Grid>
      <Grid>
        <Box marginTop="5px">
          <TextField
            size="small"
            fullWidth
            label="Help File Name"
            value={helpFileName}
            onChange={(e) => setHelpFileName(e.target.value)}
            sx={{ mb: 2 }}
          />

          {/* Hidden input, triggered by button */}
          <input
            ref={helpFileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0] || null;
              setHelpFile(f || null);
            }}
          />

          <Box display="flex" gap={1} alignItems="center">
            <Button variant="outlined" onClick={() => helpFileInputRef.current?.click()}>
              {helpFile ? 'Change File' : 'Choose File'}
            </Button>
            <Typography variant="body2" color="text.secondary">
              {helpFile ? helpFile.name : 'No file chosen'}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Accepted: .xlsx, .xls
          </Typography>
        </Box>
      </Grid>
      <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
        <Button
          variant="outlined"
          onClick={() => {
            setHelpFile(null);
            setHelpFileName('');
          }}
          size="small"
        >
          Cancel
        </Button>
        <Button variant="contained" size="small" disabled={isUploadingHelp} onClick={handleUpload}>
          {isUploadingHelp ? 'Uploading…' : 'Upload'}
        </Button>
      </Box>
      <Box mt={4}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Uploaded Help Files
        </Typography>

        <CommonTable
          columns={columns}
          data={tableData}
          enableTopToolbar={false}
          enableBottomToolbar={false}
          enablePagination={false}
          muiTableContainerProps={{ sx: { maxHeight: '360px', width: '100%' } }}
        />
      </Box>
      <ConfirmBox
        open={confirmOpen}
        onClose={handleConfirmClose}
        options={{
          title: 'Delete Help File',
          text: 'Are you sure you want to delete this help file?',
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          confirmButtonColor: 'error',
          cancelButtonColor: 'primary',
        }}
      />
    </Box>
  );
};

export default HelpFile;
